import { useState, useRef, useEffect } from 'react'
import { X, MapPin, Camera, Upload, Trash2 } from 'lucide-react'
import { CreateUserPOIData, UserPOIType } from '../../types/userPois'
import { USER_POI_TYPES } from '../../data/userPoiTypes'
import { uploadUserPOIPhoto } from '../../services/firebase/storage'
import { v4 as uuidv4 } from 'uuid'

interface AddUserPOIModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: CreateUserPOIData) => Promise<void>
  routeId: string
  coordinates: [number, number]
  initialType?: UserPOIType
  userId: string
}

export function AddUserPOIModal({
  isOpen,
  onClose,
  onSubmit,
  routeId,
  coordinates,
  initialType,
  userId
}: AddUserPOIModalProps) {
  const [type, setType] = useState<UserPOIType>(initialType || 'hazard')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [expiryDays, setExpiryDays] = useState<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [photos, setPhotos] = useState<File[]>([])
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([])
  const [uploadingPhotos, setUploadingPhotos] = useState(false)
  const [viewportScale, setViewportScale] = useState(1)
  
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Update viewport scale based on window size
  useEffect(() => {
    const updateScale = () => {
      // Responsive scaling based on screen width
      let scale = 1 // Default scale for desktop
      
      if (window.innerWidth < 640) { // Mobile phones
        scale = 0.65
      } else if (window.innerWidth < 768) { // Small tablets
        scale = 0.75
      } else if (window.innerWidth < 1024) { // Tablets
        scale = 0.85
      }
      
      setViewportScale(scale)
    }

    updateScale()
    window.addEventListener('resize', updateScale)
    return () => window.removeEventListener('resize', updateScale)
  }, [])

  if (!isOpen) return null

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    const imageFiles = files.filter(file => file.type.startsWith('image/'))
    
    if (imageFiles.length !== files.length) {
      setError('Only image files are allowed')
      return
    }

    // Limit to 5 photos
    if (photos.length + imageFiles.length > 5) {
      setError('Maximum 5 photos allowed')
      return
    }

    // Create previews
    const newPreviews: string[] = []
    imageFiles.forEach(file => {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result) {
          newPreviews.push(e.target.result as string)
          if (newPreviews.length === imageFiles.length) {
            setPhotoPreviews([...photoPreviews, ...newPreviews])
          }
        }
      }
      reader.readAsDataURL(file)
    })

    setPhotos([...photos, ...imageFiles])
    setError(null)
  }

  const handleRemovePhoto = (index: number) => {
    setPhotos(photos.filter((_, i) => i !== index))
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!name.trim()) {
      setError('Please enter a title')
      return
    }

    if (!description.trim()) {
      setError('Please enter a description')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      let photoUrls: string[] = []
      
      // Upload photos if any
      if (photos.length > 0) {
        setUploadingPhotos(true)
        const tempPoiId = uuidv4() // Generate temporary ID for photo storage
        
        const uploadPromises = photos.map(photo => 
          uploadUserPOIPhoto(userId, tempPoiId, photo)
        )
        
        try {
          photoUrls = await Promise.all(uploadPromises)
        } catch (photoError) {
          console.error('Error uploading photos:', photoError)
          setError('Failed to upload photos. Please try again.')
          setUploadingPhotos(false)
          setIsSubmitting(false)
          return
        }
        setUploadingPhotos(false)
      }

      const poiData: CreateUserPOIData = {
        routeId,
        type,
        name: name.trim(),
        description: description.trim(),
        coordinates: {
          lng: coordinates[0],
          lat: coordinates[1]
        },
        photos: photoUrls
      }

      // Add expiry date if specified
      if (expiryDays && (type === 'hazard' || type === 'condition' || type === 'roadworks')) {
        const expiresAt = new Date()
        expiresAt.setDate(expiresAt.getDate() + expiryDays)
        poiData.expiresAt = expiresAt
      }

      await onSubmit(poiData)
      
      // Reset form and close
      setType('hazard')
      setName('')
      setDescription('')
      setExpiryDays(null)
      setPhotos([])
      setPhotoPreviews([])
      onClose()
    } catch (err) {
      console.error('Error submitting POI:', err)
      setError('Failed to add report. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div 
        className="bg-gray-800 rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col"
        style={{ 
          transform: `scale(${viewportScale})`,
          transformOrigin: 'center'
        }}
      >
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <MapPin className="w-5 h-5 text-emerald-500" />
            Add Route Report
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} id="user-poi-form" className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Report Type
            </label>
            <div className="grid grid-cols-4 gap-2">
              {USER_POI_TYPES.map(poiType => {
                const Icon = poiType.icon
                const isSelected = type === poiType.id
                
                return (
                  <button
                    key={poiType.id}
                    type="button"
                    onClick={() => setType(poiType.id as UserPOIType)}
                    className={`
                      p-3 rounded-lg border-2 transition-all
                      ${isSelected 
                        ? 'border-emerald-500 bg-emerald-900/20' 
                        : 'border-gray-600 hover:border-gray-500'
                      }
                    `}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <Icon 
                        className="w-5 h-5" 
                        style={{ color: isSelected ? poiType.color : '#9CA3AF' }}
                      />
                      <span className={`text-xs font-medium ${isSelected ? 'text-emerald-400' : 'text-gray-300'}`}>
                        {poiType.name}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {USER_POI_TYPES.find(t => t.id === type)?.description}
            </p>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="poi-name" className="block text-sm font-medium text-gray-200 mb-2">
              Title
            </label>
            <input
              id="poi-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder={
                type === 'hazard' ? 'e.g., Tree down across trail' :
                type === 'condition' ? 'e.g., Muddy after rain' :
                type === 'roadworks' ? 'e.g., Lane closure on Main St' :
                'e.g., Great water refill spot'
              }
              maxLength={50}
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="poi-description" className="block text-sm font-medium text-gray-200 mb-2">
              Description
            </label>
            <textarea
              id="poi-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              rows={4}
              placeholder="Provide more details..."
              required
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-2">
              Photos (Optional)
            </label>
            
            {/* Photo Previews */}
            {photoPreviews.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {photoPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={preview}
                      alt={`Photo ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(index)}
                      className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Upload Button */}
            {photos.length < 5 && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-sm"
                >
                  <Camera className="w-4 h-4" />
                  Add Photos ({photos.length}/5)
                </button>
              </div>
            )}
          </div>

          {/* Validity Period (for hazards, conditions, and roadworks) */}
          {(type === 'hazard' || type === 'condition' || type === 'roadworks') && (
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Valid For
              </label>
              <div className="flex gap-2">
                {[
                  { days: 30, label: '1m' },
                  { days: 90, label: '3m' },
                  { days: 180, label: '6m' },
                  { days: 365, label: '12m' }
                ].map(({ days, label }) => (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setExpiryDays(expiryDays === days ? null : days)}
                    className={`
                      px-3 py-1.5 rounded-lg text-sm transition-colors
                      ${expiryDays === days 
                        ? 'bg-emerald-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                    `}
                  >
                    {label}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setExpiryDays(null)}
                  className={`
                    px-3 py-1.5 rounded-lg text-sm transition-colors
                    ${expiryDays === null 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }
                  `}
                >
                  Permanent
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                How long this {type} is expected to last
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-700 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="user-poi-form"
            className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting || !name.trim() || !description.trim()}
          >
            {uploadingPhotos ? 'Uploading photos...' : isSubmitting ? 'Adding...' : 'Add Report'}
          </button>
        </div>
      </div>
    </div>
  )
}